/**
 * Customer "My Warranties" tab — The Indian Pest Control.
 * Shows certificates, expiry countdown, PDF download, raise claim → support ticket.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Download, Loader2, X, CheckCircle2, AlertCircle, Bug,
  Calendar, Clock, FileText, MessageSquare,
} from 'lucide-react';
import { Button } from './ui/button';
import { useCustomerAuth, formatApiError } from '../contexts/CustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customers/me/warranties`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

function CountdownBadge({ days, status }) {
  if (status === 'cancelled') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-800 border border-rose-300"><AlertCircle size={10} /> Cancelled</span>;
  }
  if (status === 'stopped') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-300"><Clock size={10} /> On Hold</span>;
  }
  if (status === 'expired' || (typeof days === 'number' && days < 0)) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200"><AlertCircle size={10} /> Expired</span>;
  }
  if (typeof days === 'number' && days <= 30) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"><Clock size={10} /> {days} days left</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle2 size={10} /> {typeof days === 'number' ? `${days} days left` : 'Active'}</span>;
}

export default function CustomerWarrantiesTab() {
  const { token } = useCustomerAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [claiming, setClaiming] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(API, { headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data?.items || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const downloadPDF = async (w) => {
    try {
      const r = await axios.get(`${API}/${w.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `TheIndianPestControl_Warranty_${w.warranty_number.replace(/.*-/, '')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiError(e)); }
  };

  const activeCount = rows.filter(r => r.status === 'active').length;
  const expiringSoon = rows.filter(r => r.status === 'active' && typeof r.days_remaining === 'number' && r.days_remaining <= 30 && r.days_remaining >= 0).length;

  return (
    <div className="space-y-5" data-testid="customer-warranties-tab">
      <div>
        <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-700" /> My Warranties</h2>
        <p className="text-xs sm:text-sm text-slate-500">Active service warranties from <b>The Indian Pest Control</b>. Raise a claim anytime within the coverage period.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Active" value={activeCount} Icon={CheckCircle2} tone="emerald" />
        <SummaryCard label="Expiring Soon" value={expiringSoon} Icon={Clock} tone="amber" />
        <SummaryCard label="Total" value={rows.length} Icon={ShieldCheck} tone="slate" />
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}

      {/* Warranty period change notifications */}
      {rows.filter(r => r.last_change?.action === 'reduce').map(r => (
        <div key={`notice-${r.id}`} className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid={`warranty-reduced-notice-${r.id}`}>
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-amber-700" />
          <div>
            <span className="font-bold">Warranty Period Updated</span> — Your warranty <b>{r.warranty_number}</b> period has been updated by EntoRid. New expiry date: <b>{fmtDate(r.end_date)}</b>.
            {r.last_change?.reason && <div className="mt-0.5 text-xs text-amber-700">Reason: {r.last_change.reason}</div>}
          </div>
        </div>
      ))}

      {loading && <div className="text-center text-slate-400 py-10"><Loader2 className="inline animate-spin" size={20} /></div>}
      {!loading && rows.length === 0 && <div className="text-center text-slate-400 py-12 bg-white border border-slate-200 rounded-2xl">No warranties yet. They appear here once your service is admin-approved.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(w => (
          <div key={w.id} className="bg-white border border-slate-200 rounded-2xl p-4 relative overflow-hidden" data-testid={`cust-war-${w.id}`}>
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-full opacity-50" />
            <div className="relative">
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Warranty</div>
                  <div className="font-bold text-slate-900 truncate text-sm">{w.warranty_number}</div>
                </div>
                <CountdownBadge days={w.days_remaining} status={w.status} />
              </div>
              <div className="space-y-2 mb-3">
                <div className="flex items-start gap-2">
                  <Bug size={14} className="text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] text-slate-500 uppercase font-semibold">Treatment</div>
                    <div className="text-sm font-semibold text-slate-900 truncate">{w.pest_name}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar size={14} className="text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 grid grid-cols-2 gap-3 w-full">
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase font-semibold">From</div>
                      <div className="text-xs text-slate-700">{fmtDate(w.start_date)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase font-semibold">Until</div>
                      <div className="text-xs font-bold text-slate-900">{fmtDate(w.end_date)}</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ShieldCheck size={14} className="text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase font-semibold">Coverage</div>
                    <div className="text-sm font-semibold text-slate-900">{w.warranty_value} {w.warranty_unit}</div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => downloadPDF(w)} className="flex-1" data-testid={`cust-war-dl-${w.id}`}><Download size={13} className="mr-1.5" /> PDF</Button>
                {w.status === 'active' && (
                  <Button size="sm" onClick={() => setClaiming(w)} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid={`cust-war-claim-${w.id}`}><MessageSquare size={13} className="mr-1.5" /> Raise Claim</Button>
                )}
              </div>

              {/* Last admin action — reason shown to customer */}
              {w.last_change && (
                <div className={`mt-2 rounded-lg border p-2.5 text-xs ${w.status === 'cancelled' ? 'bg-rose-50 border-rose-200' : w.status === 'stopped' ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50 border-indigo-200'}`} data-testid={`cust-war-note-${w.id}`}>
                  <div className="font-bold uppercase text-[10px] tracking-wide mb-0.5">
                    {w.status === 'cancelled' && '⚠️ Warranty Cancelled'}
                    {w.status === 'stopped' && '⏸ Warranty Paused'}
                    {w.last_change.action === 'extend' && '✅ Warranty Extended'}
                    {w.last_change.action === 'reduce' && '🔻 Warranty Reduced'}
                    {w.last_change.action === 'reactivate' && '✅ Warranty Reactivated'}
                    {w.last_change.action === 'edit' && '✏️ Warranty Updated'}
                  </div>
                  <div className="text-slate-700">{w.last_change.reason}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">— {w.last_change.actor} · {new Date(w.last_change.ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {claiming && <ClaimModal warranty={claiming} token={token} onClose={() => setClaiming(null)} onDone={() => { setClaiming(null); alert('Claim submitted! Our team will respond shortly.'); }} />}
    </div>
  );
}

function SummaryCard({ label, value, Icon, tone }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div className={`border rounded-xl p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</span>
        <Icon size={14} className="opacity-60" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function ClaimModal({ warranty, token, onClose, onDone }) {
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 10) { setErr('Please describe the issue (at least 10 characters)'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/${warranty.id}/claim`, { description }, { headers: { Authorization: `Bearer ${token}` } });
      onDone();
    } catch (e2) { setErr(e2.response?.data?.detail || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="claim-modal">
      <form onSubmit={submit} className="bg-white w-full sm:max-w-lg rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 bg-amber-600 text-white">
          <div><div className="text-xs opacity-90">Warranty {warranty.warranty_number}</div><div className="font-bold">Raise a Warranty Claim</div></div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs">
            <div className="font-semibold text-amber-900">{warranty.pest_name}</div>
            <div className="text-amber-800">Valid until {fmtDate(warranty.end_date)} · {warranty.days_remaining} days remaining</div>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1 block">Describe the issue *</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="e.g., Cockroaches returned in the kitchen 3 days ago after the treatment." data-testid="claim-desc" />
          </div>
          {err && <div className="text-sm text-rose-700">{err}</div>}
        </div>
        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={busy} data-testid="claim-submit">
            {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Submitting…</> : 'Submit Claim'}
          </Button>
        </div>
      </form>
    </div>
  );
}
